import { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';

// Kind tabs. A person can hold several at once — an owner of one flat who
// rents another and sits on the committee — so these filter rather than
// classify. "Tenants" is what makes "notify specific tenants" one click.
const KINDS = [
    { value: '', label: 'All' },
    { value: 'OWNER', label: 'Owners' },
    { value: 'TENANT', label: 'Tenants' },
    { value: 'EMPLOYEE', label: 'Employees' },
    { value: 'COMMITTEE', label: 'Committee' },
];

const KIND_STYLE = {
    OWNER: 'tw-bg-emerald-100 tw-text-emerald-700',
    TENANT: 'tw-bg-amber-100 tw-text-amber-700',
    RESIDENT: 'tw-bg-slate-100 tw-text-slate-600',
    EMPLOYEE: 'tw-bg-sky-100 tw-text-sky-700',
    COMMITTEE: 'tw-bg-violet-100 tw-text-violet-700',
};

// Searchable multi-select over the society's members.
//
// Members come from /api/notices/audience-options, which returns the whole
// roster in one shot along with each person's kinds and flats — so filtering
// happens in memory rather than round-tripping on every keystroke. Societies
// are hundreds of members, not millions; if that stops being true this should
// move to a server-side search.
const MemberPicker = ({
    members = [],
    selected = [],
    onChange,
    loading = false,
    error = null,
}) => {
    const [query, setQuery] = useState('');
    const [kind, setKind] = useState('');

    const selectedSet = useMemo(() => new Set(selected.map(Number)), [selected]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return members.filter((m) => {
            if (kind && !(m.kinds || []).includes(kind)) return false;
            if (!q) return true;
            const haystack = [m.label, m.name, m.email, m.phone, ...(m.flats || []).map(f => f.label)]
                .filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        });
    }, [members, query, kind]);

    const toggle = (userId) => {
        const id = Number(userId);
        onChange(selectedSet.has(id)
            ? selected.filter((u) => Number(u) !== id)
            : [...selected, id]);
    };

    // "Select all" acts on what's currently filtered, not the whole roster —
    // otherwise searching then selecting all would quietly grab everyone.
    const allFilteredSelected = filtered.length > 0 && filtered.every((m) => selectedSet.has(Number(m.user_id)));
    const toggleAllFiltered = () => {
        const ids = filtered.map((m) => Number(m.user_id));
        if (allFilteredSelected) {
            const drop = new Set(ids);
            onChange(selected.filter((u) => !drop.has(Number(u))));
        } else {
            onChange([...new Set([...selected.map(Number), ...ids])]);
        }
    };

    const selectedMembers = useMemo(
        () => members.filter((m) => selectedSet.has(Number(m.user_id))),
        [members, selectedSet]
    );

    return (
        <div className={`tw-border tw-rounded-md tw-overflow-hidden ${error ? 'tw-border-rose-400' : 'tw-border-gray-200'}`}>
            <div className="tw-flex tw-flex-wrap tw-gap-1 tw-px-3 tw-pt-2 tw-bg-gray-50">
                {KINDS.map((k) => {
                    const count = k.value
                        ? members.filter((m) => (m.kinds || []).includes(k.value)).length
                        : members.length;
                    return (
                        <button
                            key={k.value || 'all'}
                            type="button"
                            onClick={() => setKind(k.value)}
                            className={`tw-px-2.5 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-transition-colors ${kind === k.value
                                ? 'tw-bg-sky-600 tw-text-white'
                                : 'tw-bg-white tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-gray-50'}`}
                        >
                            {k.label} <span className="tw-opacity-60">{count}</span>
                        </button>
                    );
                })}
            </div>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-gray-50 tw-border-b tw-border-gray-100">
                <div className="tw-relative tw-flex-1 tw-min-w-[180px]">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name, email, phone or flat"
                        className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-border-sky-500"
                    />
                </div>
                <button
                    type="button"
                    onClick={toggleAllFiltered}
                    disabled={filtered.length === 0}
                    className="tw-px-2.5 tw-py-1.5 tw-text-[11px] tw-font-medium tw-text-sky-700 tw-bg-white tw-border tw-border-gray-200 tw-rounded hover:tw-bg-sky-50 disabled:tw-opacity-40"
                >
                    {allFilteredSelected ? 'Clear these' : `Select these (${filtered.length})`}
                </button>
                <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600">
                    {selected.length} selected
                </span>
            </div>

            {/* Selected chips — visible even when the search hides their rows,
                so it's always clear who is actually on the list. */}
            {selectedMembers.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-px-3 tw-py-2 tw-border-b tw-border-gray-100">
                    {selectedMembers.map((m) => (
                        <span
                            key={m.user_id}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-bg-sky-100 tw-text-sky-800"
                        >
                            {m.label}
                            <button
                                type="button"
                                onClick={() => toggle(m.user_id)}
                                className="hover:tw-text-sky-950"
                                title={`Remove ${m.label}`}
                            >
                                <Icon icon="mdi:close" className="tw-text-xs" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="tw-max-h-56 tw-overflow-y-auto">
                {loading && (
                    <div className="tw-flex tw-justify-center tw-py-6">
                        <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-gray-200 tw-border-t-sky-500" />
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <p className="tw-text-[11px] tw-text-gray-400 tw-text-center tw-py-6">
                        {members.length === 0
                            ? 'No members found for this society'
                            : `No ${kind ? kind.toLowerCase() + 's' : 'members'} match that search`}
                    </p>
                )}
                {!loading && filtered.map((m) => {
                    const id = Number(m.user_id);
                    const active = selectedSet.has(id);
                    return (
                        <label
                            key={id}
                            className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-cursor-pointer tw-border-b tw-border-gray-50 last:tw-border-0 ${active ? 'tw-bg-sky-50/60' : 'hover:tw-bg-gray-50'}`}
                        >
                            <input type="checkbox" checked={active} onChange={() => toggle(id)} />
                            <span className="tw-min-w-0 tw-flex-1">
                                <span className="tw-block tw-text-xs tw-text-gray-800 tw-truncate">{m.label}</span>
                                <span className="tw-block tw-text-[10px] tw-text-gray-400 tw-truncate">
                                    {[
                                        (m.flats || []).map(f => f.label).join(', '),
                                        m.email,
                                    ].filter(Boolean).join(' · ') || '—'}
                                </span>
                            </span>
                            <span className="tw-shrink-0 tw-flex tw-gap-1">
                                {(m.kinds || []).map((k) => (
                                    <span key={k} className={`tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-semibold ${KIND_STYLE[k] || KIND_STYLE.RESIDENT}`}>
                                        {k.charAt(0) + k.slice(1).toLowerCase()}
                                    </span>
                                ))}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

export default MemberPicker;
